import { ComponentType, useEffect, useMemo } from 'react'
import { Helmet } from 'react-helmet-async'
import { useSelector } from 'react-redux'
import { useLocation, useNavigate } from 'react-router-dom'
import { useFeatureFlagEnabled } from 'posthog-js/react'

import { isStreamUnhealthy } from './AuditLogStreamingSection/streamHealth'
import { useAuditLogStreamingStatus } from './AuditLogStreamingSection/useAuditLogStreamingStatus'
import AuditLogStreamingSection from './AuditLogStreamingSection'
import WorkosAuditEventsSection from './WorkosAuditEventsSection'

import styles from './AuditLogsPage.module.scss'

import Tabs from '@/components/Tabs'
import { selectCurrentEnterprise, selectIsAdmin, selectPlanType } from '@/store/features/user/userSlice'
import { PlanType, SettingType } from '@/types'

type TabSlug = 'events' | 'siem'

// App.tsx's audit-logs routes derive their slugs from AUDIT_LOGS_TAB_SLUGS below so a new tab
// can't be added here without a route. The SIEM tab is filtered out at render time (plan type +
// setting), not from this list, so its route still resolves — AuditLogsPage redirects away
// from it below when it's not currently visible.
const AUDIT_LOGS_TABS: Array<{ label: string; slug: TabSlug; Body: ComponentType }> = [
  { label: 'Events', slug: 'events', Body: WorkosAuditEventsSection },
  { label: 'SIEM configuration', slug: 'siem', Body: AuditLogStreamingSection },
]

export const AUDIT_LOGS_TAB_SLUGS = AUDIT_LOGS_TABS.map((tab) => tab.slug)

const AuditLogsPage = () => {
  const isAdmin = useSelector(selectIsAdmin)
  const enterprise = useSelector(selectCurrentEnterprise)
  const planType = useSelector(selectPlanType)
  const navigate = useNavigate()
  const location = useLocation()

  const auditLogStreamingUiVisible = enterprise?.settings[SettingType.auditLogStreamingUiVisible] === true
  const showStreamingSection = planType === PlanType.Enterprise && auditLogStreamingUiVisible
  // Events is the new tab added alongside this rollout flag — SIEM predates it and stays
  // reachable regardless (see App.tsx's AuditLogsRoute and Sidebar.tsx for the matching page-
  // level access, which this PR deliberately left ungated).
  const eventsEnabled = useFeatureFlagEnabled('audit-logs-ui')
  const streamStatus = useAuditLogStreamingStatus({ enabled: isAdmin && showStreamingSection })
  const streamUnhealthy = showStreamingSection && isStreamUnhealthy(streamStatus.data?.log_stream)

  const visibleTabs = useMemo(
    () =>
      AUDIT_LOGS_TABS.filter((tab) => tab.slug !== 'siem' || showStreamingSection).filter(
        (tab) => tab.slug !== 'events' || eventsEnabled === true
      ),
    [showStreamingSection, eventsEnabled]
  )

  const getActiveTab = () => {
    const index = visibleTabs.findIndex((tab) => location.pathname.endsWith(`/${tab.slug}`))
    return index === -1 ? 0 : index
  }

  const handleTabChange = (index: number) => {
    navigate(`/app/audit-logs/${visibleTabs[index].slug}`)
  }

  // Covers both the bare /app/audit-logs path and a slug that's no longer visible (e.g. the
  // SIEM tab disappeared because the plan or setting changed mid-session) — either way, fall
  // back to whatever tab IS visible. Skipped while eventsEnabled is still resolving (undefined)
  // so a flag-off admin doesn't get redirected off /events before Events is filtered out.
  useEffect(() => {
    if (eventsEnabled === undefined) return
    if (visibleTabs.length > 0 && !visibleTabs.some((tab) => location.pathname.endsWith(`/${tab.slug}`))) {
      navigate(`/app/audit-logs/${visibleTabs[0].slug}`)
    }
  }, [location.pathname, navigate, visibleTabs, eventsEnabled])

  if (!isAdmin) {
    return (
      <div className={styles.container}>
        <p>You are not authorized to access this page</p>
      </div>
    )
  }

  if (visibleTabs.length === 0) {
    return (
      <div className={styles.container}>
        <p>Audit logs aren&apos;t available for your enterprise yet.</p>
      </div>
    )
  }

  const ActiveBody = visibleTabs[getActiveTab()].Body

  return (
    <>
      <Helmet>
        <title>Wispr Flow | Audit Logs</title>
      </Helmet>
      <div className={`${styles.container} ${styles.auditLogsContainer}`}>
        <div className={`${styles.tabTitle} ${styles.pageTabTitle}`}>
          <h6>Audit Logs</h6>
          {visibleTabs.length > 1 && (
            <Tabs
              tabs={visibleTabs.map((tab) => tab.label)}
              activeTab={getActiveTab()}
              setActiveTab={handleTabChange}
            />
          )}
        </div>

        {streamUnhealthy && visibleTabs[getActiveTab()].slug !== 'siem' && (
          <div className={styles.streamBanner} role="status">
            <span>Audit log streaming isn&apos;t delivering events.</span>
            <button type="button" className={styles.streamBannerLink} onClick={() => navigate('/app/audit-logs/siem')}>
              See what to fix
            </button>
          </div>
        )}

        <div className={styles.tabbedContent}>
          <ActiveBody />
        </div>
      </div>
    </>
  )
}

export default AuditLogsPage
